import {
    Column,
    Container,
    cssVar,
    DropDown,
    Grid,
    Header,
    Label,
    Link,
    Row,
    useAlignments,
    useScreen,
    useSizes,
} from 'metrix-common3';
// biome-ignore lint/correctness/noUnusedImports: React must be in scope for JSX
import React, { useEffect, useState } from 'react';
import { useIntl } from 'react-intl';
import styled from 'styled-components';

const BORDEREAU_PRODUCT_REFERENCE_CODE = 'CustomReference|Bordereau|Product';

const HeaderContainer = styled.div`
    display: flex;
    justify-content: center;
    align-items: center;
    margin: 3rem 0;
`;

const ContentContainer = styled.div`
    display: flex;
    flex-direction: column;
    align-items: center;
    width: 100%;
`;

const DropdownWrapper = styled.div`
    width: 100%;
    max-width: 300px;
    margin-bottom: 2rem;
`;

const TableWrapper = styled.div`
    width: 100%;
    max-width: 1000px;
`;

const TableContainer = styled(Container)`
    border: 1px solid ${cssVar.colorStrokeLight};
    border-radius: 4px;
`;

const CustomRow = styled(Row)`
    border-bottom: 1px solid ${cssVar.colorStrokeLight};

    :last-child {
        border-bottom: none;
    }
`;

const CustomLabel = styled(Label)`
    ${(props) =>
        props.$desktop &&
        `
        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;
    `}
`;

const Bordereaux = (props) => {
    const alignments = useAlignments();
    const sizes = useSizes();
    const screen = useScreen();
    const intl = useIntl();

    const [selectedProduct, setSelectedProduct] = useState(null);
    const tablet = screen?.isTablet;
    const desktop = screen?.isDesktop;

    // biome-ignore lint/correctness/useExhaustiveDependencies: intentionally run only on mount
    useEffect(() => {
        props.doReduceView('bordereaux');
        // Load custom reference for products dropdown
        props.reference.doGetReferences({
            code: BORDEREAU_PRODUCT_REFERENCE_CODE,
            custom: true,
        });
    }, []);

    // biome-ignore lint/correctness/useExhaustiveDependencies: callback reference is stable
    useEffect(() => {
        if (selectedProduct) {
            props.doGetBordereauSummaries(selectedProduct);
        }
    }, [selectedProduct]);

    const handleProductChange = (value) => {
        setSelectedProduct(value?.code || null);
    };

    const handleDownload = (guid) => {
        props.doGetBordereauDownloadUrl(guid);
    };

    const titleText = intl.formatMessage({ id: 'Policies_Bordereaux.title' });
    const selectProductText = intl.formatMessage({ id: 'Policies_Bordereaux.filter.selectProduct' });
    const yearText = intl.formatMessage({ id: 'Policies_Bordereaux.table.year' });
    const monthText = intl.formatMessage({ id: 'Policies_Bordereaux.table.month' });
    const countText = intl.formatMessage({ id: 'Policies_Bordereaux.table.count' });
    const downloadText = intl.formatMessage({ id: 'Policies_Bordereaux.table.download' });
    const noDataText = intl.formatMessage({ id: 'Policies_Bordereaux.noData' });

    const products = props.reference?.references?.[BORDEREAU_PRODUCT_REFERENCE_CODE] || [];
    const summaries = props.bordereauSummaries || [];

    const getMonthName = (monthNumber) => {
        const date = new Date(2000, monthNumber - 1, 1);
        return date.toLocaleString(intl.locale, { month: 'long' });
    };

    return (
        <Container>
            <HeaderContainer>
                <Header text={titleText} size={desktop || tablet ? sizes.xl : sizes.lg} />
            </HeaderContainer>
            <ContentContainer>
                <DropdownWrapper>
                    <DropDown
                        placeholder={selectProductText}
                        value={selectedProduct ? { code: selectedProduct } : null}
                        references={products}
                        referencesEvent={() =>
                            props.reference.doGetReferences({
                                code: BORDEREAU_PRODUCT_REFERENCE_CODE,
                                custom: true,
                            })
                        }
                        changeEvent={handleProductChange}
                        alwaysFetch={false}
                    />
                </DropdownWrapper>

                {selectedProduct && (
                    <TableWrapper>
                        <TableContainer top={sizes.sm} bottom={sizes.xl} color={cssVar.colorLightMode}>
                            <Grid>
                                <CustomRow nogutter>
                                    <Column xs={3} md={3} nogutter>
                                        <Label
                                            top={sizes.xs}
                                            left={sizes.sm}
                                            bottom={sizes.xs}
                                            textAlign={alignments.left}
                                            size={sizes.lg}
                                            bold
                                            text={yearText}
                                        />
                                    </Column>
                                    <Column xs={3} md={3} nogutter>
                                        <Label
                                            top={sizes.xs}
                                            left={sizes.sm}
                                            bottom={sizes.xs}
                                            textAlign={alignments.left}
                                            size={sizes.lg}
                                            bold
                                            text={monthText}
                                        />
                                    </Column>
                                    <Column xs={3} md={3} nogutter>
                                        <Label
                                            top={sizes.xs}
                                            left={sizes.sm}
                                            bottom={sizes.xs}
                                            textAlign={alignments.left}
                                            size={sizes.lg}
                                            bold
                                            text={countText}
                                        />
                                    </Column>
                                    <Column xs={3} md={3} nogutter />
                                </CustomRow>
                                {summaries.length === 0 && (
                                    <CustomRow nogutter>
                                        <Column xs={12} nogutter>
                                            <Container
                                                top={sizes.md}
                                                bottom={sizes.md}
                                                horizontalAlignment={alignments.center}
                                            >
                                                <Label text={noDataText} size={sizes.lg} />
                                            </Container>
                                        </Column>
                                    </CustomRow>
                                )}
                                {summaries.map((summary, index) => (
                                    <CustomRow
                                        // biome-ignore lint/suspicious/noArrayIndexKey: composite key includes index as a tiebreaker for deterministic-ordered summaries
                                        key={`${summary.productCode}-${summary.year}-${summary.month}-${index}`}
                                        nogutter
                                    >
                                        <Column xs={3} md={3} nogutter>
                                            <CustomLabel
                                                top={sizes.xs}
                                                left={sizes.sm}
                                                bottom={sizes.xs}
                                                textAlign={alignments.left}
                                                $desktop={desktop}
                                                size={sizes.lg}
                                                text={String(summary.year)}
                                                title={String(summary.year)}
                                            />
                                        </Column>
                                        <Column xs={3} md={3} nogutter>
                                            <CustomLabel
                                                top={sizes.xs}
                                                left={sizes.sm}
                                                bottom={sizes.xs}
                                                textAlign={alignments.left}
                                                $desktop={desktop}
                                                size={sizes.lg}
                                                text={getMonthName(summary.month)}
                                                title={getMonthName(summary.month)}
                                            />
                                        </Column>
                                        <Column xs={3} md={3} nogutter>
                                            <CustomLabel
                                                top={sizes.xs}
                                                left={sizes.sm}
                                                bottom={sizes.xs}
                                                textAlign={alignments.left}
                                                $desktop={desktop}
                                                size={sizes.lg}
                                                text={String(summary.count ?? 0)}
                                                title={String(summary.count ?? 0)}
                                            />
                                        </Column>
                                        <Column xs={3} md={3} nogutter>
                                            <CustomLabel
                                                text={
                                                    summary.hasFile && summary.guid ? (
                                                        <Link
                                                            clickEvent={() => handleDownload(summary.guid)}
                                                            size={sizes.lg}
                                                            textAlign={alignments.left}
                                                            inline
                                                        >
                                                            {downloadText}
                                                        </Link>
                                                    ) : (
                                                        '-'
                                                    )
                                                }
                                                top={sizes.xs}
                                                left={sizes.sm}
                                                bottom={sizes.xs}
                                                textAlign={alignments.left}
                                                $desktop={desktop}
                                                size={sizes.lg}
                                            />
                                        </Column>
                                    </CustomRow>
                                ))}
                            </Grid>
                        </TableContainer>
                    </TableWrapper>
                )}
            </ContentContainer>
        </Container>
    );
};

export default Bordereaux;
